import { Box, Paper, Tab, Tabs } from '@mui/material';
import { FC, useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { COLORS } from 'services/constants/colors';
import { isPathAllowed, PATHS } from 'routes/constants';
import { getUserInfo } from 'redux/reducers/user/selectors';
import { TabGroupMenu } from './TabGroupMenu';
import {
	ALL_TAB_VALUES,
	DEFAULT_TAB,
	GROUPED_TAB_VALUES,
	PLAIN_TABS,
	TAB_GROUPS,
} from './constants';
import { AdditionalServices } from './AdditionalServices';
import { GeneralContractDocuments } from './GeneralContractDocuments';
import { EmailTemplates } from './EmailTemplates';
import { EuDdpTariffs } from './EuDdpTariffs';
import { EuDdpDeliveryOperators } from './EuDdpDeliveryOperators/';
import { EuDdpFees } from './EuDdpFees';
import { ShopifySalesChannels } from './ShopifySalesChannels';
import { EasProjectSettings } from './EasProjectSettings';
import { AnnualFeeTypes } from './AnnualFeeTypes';
import { MonthlyFees } from './MonthlyFees';
import { InvoicingFeeTypes } from './InvoicingFeeTypes';
import { FeeClasses } from './FeeClasses';
import { Enums } from './Enums';
import { Countries } from './Countries';
import { Currencies } from './Currencies';
import { SupportedPostalOperators } from './SupportedPostalOperators';
import { DailyExchangeRates } from './ExchangeRates/DailyExchangeRates';
import { AnnualExchangeRates } from './ExchangeRates/AnnualExchangeRates';
import { GbpMonthlyExchangeRates } from './ExchangeRates/GbpMonthlyExchangeRates';
import { EuMonthlyExchangeRates } from './ExchangeRates/EuMonthlyExchangeRates';
import { UkExchangeRates } from './ExchangeRates/UkExchangeRates';
import { EuVatRates } from './TaxDuties/EuVatRates';
import { NonEuVatRates } from './TaxDuties/NonEuVatRates';
import { Hs6pCodes } from './TaxDuties/Hs6pCodes';
import { EuCustomsDutyRates } from './TaxDuties/EuCustomsDutyRates';
import { EuSpecialVatTerritories } from './TaxDuties/EuSpecialVatTerritories';
import { PackagingTypes } from './Ppwr/PackagingTypes';
import { EprServiceFees } from './Ppwr/EprServiceFees';
import { EPpwrTabGroup, ETaxDutiesTabGroup } from './constants';

export const ReferenceTables: FC = () => {
	const { tab } = useParams();
	// The URL is the single source of truth for the selected tab.
	const value = tab || DEFAULT_TAB;
	const navigate = useNavigate();
	const roles = useSelector(getUserInfo)?.roles;

	// A tab is visible only when its path (`/reference-tables/<tab>`) is granted
	// to one of the user's roles in the nav tree (admin sees every tab). The root
	// `/reference-tables` entry controls only the header link, not the tabs.
	const isTabAllowed = useCallback(
		(tabValue: string) => isPathAllowed(`${PATHS.REFERENCE_TABLES_PAGE}/${tabValue}`, roles),
		[roles],
	);

	const plainTabs = useMemo(() => PLAIN_TABS.filter((t) => isTabAllowed(t.value)), [isTabAllowed]);
	const tabGroups = useMemo(
		() =>
			TAB_GROUPS.map((group) => ({
				...group,
				items: group.items.filter((item) => isTabAllowed(item.value)),
			})).filter((group) => group.items.length > 0),
		[isTabAllowed],
	);

	const selectTab = useCallback(
		(newValue: string) => {
			navigate(`${PATHS.REFERENCE_TABLES_PAGE}/${newValue}`, { replace: true });
		},
		[navigate],
	);

	// Once roles are known, redirect to the first allowed tab if the current
	// one (from the URL or the default) is not permitted.
	useEffect(() => {
		if (!roles || roles.length === 0) return;
		if (isTabAllowed(value)) return;
		const firstAllowed = ALL_TAB_VALUES.find(isTabAllowed);
		if (firstAllowed) selectTab(firstAllowed);
	}, [roles, value, isTabAllowed, selectTab]);

	const isValueAllowed = isTabAllowed(value);

	const handleChange = (event: React.SyntheticEvent, newValue: string) => {
		selectTab(newValue);
	};

	// Tabs collapsed into a group have no `Tab` child, so the bar must show no selection for them.
	const isGroupedTab = GROUPED_TAB_VALUES.includes(value);

	return (
		<Box sx={{ margin: '0px 10px 0 10px' }}>
			<Box sx={{ borderBottom: 0, borderColor: 'divider' }}>
				<Paper
					sx={{
						m: '-12px -10px 10px -10px',
						borderRadius: '0!important',
					}}
				>
					<Box sx={{ display: 'flex', alignItems: 'center', p: '4px 8px' }}>
						<Tabs
							value={isGroupedTab || !isValueAllowed ? false : value}
							onChange={handleChange}
							aria-label="basic tabs example"
							sx={{
								minHeight: '20px',
								'& .MuiTabs-indicator': {
									backgroundColor: 'primary.main',
									height: '0',
								},
								'& .MuiButtonBase-root': {
									fontWeight: 'bold',
									color: COLORS.PRIMARY,
									opacity: 1,
								},
								'& .Mui-selected': {
									color: COLORS.PRIMARY,
									opacity: 0.5,
								},
							}}
						>
							{plainTabs.map((t) => (
								<Tab
									key={t.value}
									value={t.value}
									label={t.label}
									sx={{ fontWeight: 'bold', p: '0 10px', minHeight: '20px' }}
								/>
							))}
						</Tabs>
						{tabGroups.map((group) => (
							<TabGroupMenu
								key={group.name}
								name={group.name}
								items={group.items}
								selectedValue={value}
								onItemClick={selectTab}
							/>
						))}
					</Box>
				</Paper>
			</Box>
			{isValueAllowed && (
				<Box>
					{value === 'additional-services' && <AdditionalServices />}
					{value === 'general-contract-documents' && <GeneralContractDocuments />}
					{value === 'email-templates' && <EmailTemplates />}
					{value === 'euDdpTariffs' && <EuDdpTariffs />}
					{value === 'euDdpDeliveryOperators' && <EuDdpDeliveryOperators />}
					{value === 'euDdpFees' && <EuDdpFees />}
					{value === 'shopify-sales-channels' && <ShopifySalesChannels />}
					{value === 'eas-project-settings' && <EasProjectSettings />}
					{value === 'enums' && <Enums />}
					{value === 'countries' && <Countries />}
					{value === 'currencies' && <Currencies />}
					{value === 'supported-postal-operators' && <SupportedPostalOperators />}
					{value === 'daily-exchange-rates' && <DailyExchangeRates />}
					{value === 'annual-exchange-rates' && <AnnualExchangeRates />}
					{value === 'eu-monthly-exchange-rates' && <EuMonthlyExchangeRates />}
					{value === 'gbp-monthly-exchange-rates' && <GbpMonthlyExchangeRates />}
					{value === 'uk-exchange-rates' && <UkExchangeRates />}
					{value === ETaxDutiesTabGroup.EU_VAT_RATES && <EuVatRates />}
					{value === ETaxDutiesTabGroup.NON_EU_VAT_RATES && <NonEuVatRates />}
					{value === ETaxDutiesTabGroup.HS6P_CODES && <Hs6pCodes />}
					{value === ETaxDutiesTabGroup.EU_CUSTOMS_DUTY_RATES && <EuCustomsDutyRates />}
					{value === ETaxDutiesTabGroup.EU_SPECIAL_VAT_TERRITORIES && <EuSpecialVatTerritories />}
					{value === 'fee-classes' && <FeeClasses />}
					{value === 'monthly-fees' && <MonthlyFees />}
					{value === 'annual-fee-types' && <AnnualFeeTypes />}
					{value === 'invoicing-fee-types' && <InvoicingFeeTypes />}
					{value === EPpwrTabGroup.PACKAGING_TYPES && <PackagingTypes />}
					{value === EPpwrTabGroup.EPR_SERVICE_FEES && <EprServiceFees />}
				</Box>
			)}
		</Box>
	);
};
